import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import type { ComponentType } from "react";
import {
  LuFileText,
  LuTruck,
  LuStore,
  LuMegaphone,
  LuBuilding2,
  LuLock,
  LuClock,
  LuWrench,
  LuBedDouble,
  LuChevronRight,
} from "react-icons/lu";
import { Toast } from "../components/common/toastMessegeComponent";
import API from "../API/API";
import {
  mobile,
  tabletP,
  tabletL,
  pc,
  tablet,
  paddingX,
} from "../styles/responsive";
import { FONT, responsiveFont } from "../styles/typography";

type MenuItem = {
  key: string;
  title: string;
  desc: string;
  Icon: ComponentType<{ className?: string }>;
  color: string;
  onClick: () => void;
  disabled?: boolean;
};

// react-icons v5의 IconType(=> ReactNode)이 @types/react 18.2의 JSX 타입과
// 충돌하므로, 렌더 가능한 컴포넌트 타입으로 캐스팅해 사용한다.
const asIcon = (C: unknown) => C as ComponentType<{ className?: string }>;
const ChevronIcon = asIcon(LuChevronRight);

/* ----------------------------- layout ------------------------------ */

const Page = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const Inner = styled.div`
  width: 100%;
  box-sizing: border-box;
  margin: 0 auto;

  /* 좌우 패딩: 헤더와 동일 단일 소스 (styles/responsive) */
  ${paddingX}

  /* PC만 가독성을 위해 max-width 중앙 정렬, 태블릿/모바일은 풀폭(헤더와 정렬) */
  ${pc} {
    max-width: 1100px;
    padding-top: 48px;
    padding-bottom: 64px;
  }
  ${tabletL} {
    padding-top: 40px;
    padding-bottom: 56px;
  }
  ${tabletP} {
    padding-top: 36px;
    padding-bottom: 48px;
  }
  ${mobile} {
    padding-top: 24px;
    padding-bottom: 40px;
  }
`;

const Greeting = styled.div`
  ${mobile} {
    margin-bottom: 18px;
  }
  ${tabletP} {
    margin-bottom: 24px;
  }
  ${tabletL} {
    margin-bottom: 28px;
  }
  ${pc} {
    margin-bottom: 32px;
  }
`;

const Hello = styled.h1`
  margin: 0;
  font-weight: 800;
  color: #20242a;
  letter-spacing: -0.4px;

  span {
    color: #ff7f3b;
  }

  ${mobile} {
    font-size: 22px;
  }
  ${tabletP} {
    font-size: 26px;
  }
  ${tabletL} {
    font-size: 28px;
  }
  ${pc} {
    font-size: 32px;
  }
`;

const Sub = styled.p`
  margin: 8px 0 0;
  color: #8b9098;
  font-weight: 500;

  ${mobile} {
    font-size: 13px;
  }
  ${tablet} {
    font-size: 14px;
  }
  ${pc} {
    font-size: 15px;
  }
`;

const Grid = styled.div`
  display: grid;
  width: 100%;

  ${pc} {
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
  }
  ${tabletL} {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }
  ${tabletP} {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
  ${mobile} {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

/* ----------------------------- card -------------------------------- */

const Card = styled.button<{ $color: string; $disabled?: boolean }>`
  --c: ${(p) => p.$color};
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  text-align: left;
  background: #ffffff;
  border: 1px solid #ededf0;
  border-radius: 18px;
  font-family: inherit;
  cursor: ${(p) => (p.$disabled ? "default" : "pointer")};
  opacity: ${(p) => (p.$disabled ? 0.6 : 1)};
  transition: transform 0.16s ease, box-shadow 0.16s ease,
    border-color 0.16s ease;

  ${pc} {
    padding: 28px;
    gap: 22px;
  }
  ${tabletL} {
    padding: 26px;
    gap: 20px;
  }
  ${tabletP} {
    padding: 26px;
    gap: 20px;
  }
  ${mobile} {
    padding: 20px;
    border-radius: 16px;
  }

  &:hover {
    ${(p) =>
      p.$disabled
        ? ""
        : `
      transform: translateY(-3px);
      border-color: var(--c);
      box-shadow: 0 14px 30px rgba(20, 24, 32, 0.1);
    `}
  }

  /* hover 시 아이콘 배지 채움 */
  &:hover .badge {
    ${(p) =>
      p.$disabled
        ? ""
        : `
      background: var(--c);
      color: #fff;
    `}
  }
  & .chev {
    flex: 0 0 auto;
    color: #c7ccd2;
    width: 24px;
    height: 24px;
    transition: transform 0.16s ease, color 0.16s ease;
    ${mobile} {
      width: 22px;
      height: 22px;
    }
  }
  &:hover .chev {
    ${(p) => (p.$disabled ? "" : `color: var(--c); transform: translateX(3px);`)}
  }
`;

const Badge = styled.span<{ $color: string }>`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  background: ${(p) => p.$color}1a; /* 12% tint */
  color: ${(p) => p.$color};
  transition: background 0.16s ease, color 0.16s ease;

  ${pc} {
    width: 64px;
    height: 64px;
  }
  ${tablet} {
    width: 60px;
    height: 60px;
  }
  ${mobile} {
    width: 54px;
    height: 54px;
    border-radius: 14px;
  }

  svg {
    ${pc} {
      width: 32px;
      height: 32px;
    }
    ${tablet} {
      width: 30px;
      height: 30px;
    }
    ${mobile} {
      width: 27px;
      height: 27px;
    }
  }
`;

const TextBox = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const Title = styled.div`
  font-weight: 700;
  color: #20242a;
  letter-spacing: -0.2px;

  /* 폰트 크기: 전역 토큰(styles/typography)에서 관리 */
  ${responsiveFont(FONT.cardTitle)}
`;

const Desc = styled.div`
  margin-top: 5px;
  color: #9097a0;
  font-weight: 500;
  line-height: 1.45;
  overflow: hidden;
  text-overflow: ellipsis;

  ${responsiveFont(FONT.cardDesc)}
`;

/* ================================================================== */

export default function LandingPage() {
  const loginUser = JSON.parse(localStorage.getItem("loginUser") || "{}");
  const [fetchStatus, setFetchStatus] = useState(false);
  const [popupContent, setPopupContent] = useState<any[]>([]);

  const navigate = useNavigate();

  const detailPageShow = () => navigate(`contractlist`);
  const sitePageShow = () => navigate(`onsitecontract`);
  const mattressPageShow = () => navigate(`mattress`);
  const plumberPageShow = () => navigate(`plumber`);
  const moveToIntroductionPage = () => navigate(`introduction`);
  const moveToEnterpriseContractList = () => navigate(`enterpriseContractList`);
  const disabledSite = () => setFetchStatus(true);

  const getPopupList = async () => {
    try {
      const response = await API.get("notice/list");
      setPopupContent(response.data.result.noticeList);
    } catch (error) {
      console.error("팝업 조회 오류:", error);
    }
  };

  useEffect(() => {
    getPopupList();
  }, []);

  useEffect(() => {
    if (popupContent.length > 0) {
      // 팝업 모달은 추후 연결
    }
  }, [popupContent]);

  const comingSoon = (key: string): MenuItem => ({
    key,
    title: "준비 중",
    desc: "서비스 준비 중입니다",
    Icon: asIcon(LuClock),
    color: "#9aa1a9",
    onClick: disabledSite,
    disabled: true,
  });

  let menu: MenuItem[];

  if (loginUser.userType === "mattress") {
    menu = [
      {
        key: "mattress",
        title: "매트리스케어",
        desc: "매트리스케어 접수 및 상품 관리",
        Icon: asIcon(LuBedDouble),
        color: "#ff7a3d",
        onClick: mattressPageShow,
      },
      comingSoon("m1"),
      comingSoon("m2"),
      comingSoon("m3"),
    ];
  } else if (loginUser.userType === "plumber") {
    menu = [
      {
        key: "plumber",
        title: "배관케어",
        desc: "배관케어 접수 및 상품 관리",
        Icon: asIcon(LuWrench),
        color: "#2d8cf0",
        onClick: plumberPageShow,
      },
      comingSoon("p1"),
      comingSoon("p2"),
      comingSoon("p3"),
    ];
  } else {
    menu = [
      {
        key: "toss",
        title: "견적 토스건",
        desc: "배정된 견적·계약·작업 리스트를 관리합니다",
        Icon: asIcon(LuFileText),
        color: "#ff7a3d",
        onClick: detailPageShow,
      },
      {
        key: "site",
        title: "지점 자체건",
        desc: "현장 자체 견적을 통인CS에 접수합니다",
        Icon: asIcon(LuTruck),
        color: "#2eb872",
        onClick: sitePageShow,
      },
      {
        key: "mall",
        title: "통인 자재몰",
        desc: "이사 자재 구매 바로가기",
        Icon: asIcon(LuStore),
        color: "#ec5151",
        onClick: () => window.open(`https://tonginexpress.com/`),
      },
      {
        key: "promo",
        title: "홍보 자료",
        desc: "홍보 자료 모아보기",
        Icon: asIcon(LuMegaphone),
        color: "#f5a623",
        onClick: moveToIntroductionPage,
      },
      loginUser.isCompanyContractTeam
        ? {
            key: "enterprise",
            title: "기업 이전",
            desc: "기업 이전 접수하기",
            Icon: asIcon(LuBuilding2),
            color: "#2d8cf0",
            onClick: moveToEnterpriseContractList,
          }
        : {
            key: "enterprise",
            title: "기업 이전",
            desc: "기업이전팀만 이용 가능합니다",
            Icon: asIcon(LuLock),
            color: "#9aa1a9",
            onClick: disabledSite,
            disabled: true,
          },
      comingSoon("soon"),
    ];
  }

  return (
    <Page>
      {fetchStatus && (
        <Toast
          text={"준비 중 입니다."}
          setFetchStatus={setFetchStatus}
          fetchStatus={fetchStatus}
          status={"FAIL"}
        />
      )}

      <Inner>
        <Greeting>
          <Hello>
            안녕하세요, <span>{loginUser.name || "회원"}</span> 님 👋
          </Hello>
          <Sub>오늘도 통인익스프레스와 함께 빠르고 안전하게 진행하세요.</Sub>
        </Greeting>

        <Grid>
          {menu.map(({ key, title, desc, Icon, color, onClick, disabled }) => (
            <Card
              key={key}
              $color={color}
              $disabled={disabled}
              onClick={onClick}
              type="button"
            >
              <Badge className="badge" $color={color}>
                <Icon />
              </Badge>
              <TextBox>
                <Title>{title}</Title>
                <Desc>{desc}</Desc>
              </TextBox>
              <ChevronIcon className="chev" />
            </Card>
          ))}
        </Grid>
      </Inner>
    </Page>
  );
}
